import { FC } from 'react';
import { WarningAmberRounded as WarningIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { ConfirmationButton, ConfirmationModal } from '@components';
import { SingularKeys, Trans } from '@libs/i18n';

interface SaveChangesModalProps {
  open: boolean;
  onSaveAndMove: () => Promise<void>;
  onDiscardAndMove: () => Promise<void>;
  onStay: () => void;
  isLoading?: boolean;
  destinationType?: 'back' | 'next' | 'exit' | 'domain' | 'resume' | 'resumeNext';
}

/**
 * Modal that warns user about unsaved changes before navigation
 * Offers 3 options:
 * 1. Save & Move - Saves current domain then navigates
 * 2. Discard & Move - Discards changes then navigates
 * 3. Cancel (Stay) - Cancels navigation and stays on current domain
 */
export const SaveChangesModal: FC<SaveChangesModalProps> = observer(({
  open,
  onSaveAndMove,
  onDiscardAndMove,
  onStay,
  isLoading = false,
}) => {

  const buttons: ConfirmationButton[] = [
    {
      label: <Trans i18nKey="assessment.workflow.unsavedChanges.cancel" defaults="Cancel" />,
      onClick: onStay,
      variant: 'outlined',
      disabled: isLoading,
      sx: {
        border: 'none',
        marginInlineEnd: '32px',
      },
    },
    {
      label: (
        <Trans
          i18nKey="assessment.workflow.unsavedChanges.discard"
        />
      ),
      onClick: onDiscardAndMove,
      variant: 'contained',
      isLoading: isLoading,
      disabled: isLoading,
      customColor: {
        backgroundColor: 'error.main',
        color: 'white',
      },
      sx: {
        boxShadow: 'none',
      }
    },
    {
      label: (
        <Trans
          i18nKey="assessment.workflow.unsavedChanges.save"
          defaults="Save & Continue"
        />
      ),
      onClick: onSaveAndMove,
      variant: 'contained',
      customColor: {
        backgroundColor: '#000000', // Black
        color: '#FFFFFF',
        hoverBackgroundColor: '#333333',
      },
      isLoading: isLoading,
      disabled: isLoading,
      sx: {
        border: '1px none #000000',
        boxShadow: 'none',
      },
    },
  ];

  return (
    <ConfirmationModal
      open={open}
      onClose={onStay}
      title={
        <Trans
          i18nKey={'assessment.workflow.unsavedChanges.title' as SingularKeys}
          defaults="Unsaved Changes"
        />
      }
      icon={<WarningIcon sx={{ fontSize: 24 }} />}
      iconColor="warning"
      buttons={buttons}
      isLoading={isLoading}
      content={
        <>
          <Typography variant="body1" color="text.secondary">
            <Trans i18nKey={'assessment.workflow.unsavedChanges.message' as SingularKeys} />
          </Typography>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.200', borderRadius: 1, border: '1px solid #D6D6D6', boxShadow: 'none' }}>
            <Typography variant="textMD_regular" color="text.primary">
              <strong><Trans i18nKey={'assessment.workflow.unsavedChanges.changesInclude' as SingularKeys} /></strong>
              <br />• <Trans i18nKey={'assessment.workflow.unsavedChanges.pendingUploads' as SingularKeys} />
              <br />• <Trans i18nKey={'assessment.workflow.unsavedChanges.pendingDeletions' as SingularKeys} />
              <br />• <Trans i18nKey={'assessment.workflow.unsavedChanges.fieldChanges' as SingularKeys} />
            </Typography>
          </Box>
        </>
      }
    />
  );
});
